// src/pages/work/WorkDetails.page.jsx
// Container (data + actions) for Work Details

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import {
  useWork,
  useWorkList,
  useUpdateWork,
  useTransitionWork,
  useDeleteWork,
  useAttachInvoice,
  useCreateWork,
  useChildWorks,
} from "../../hooks/useWorkQueries";
import { getWorkByNumber, sendWorkNotice } from "../../services/workService";
import { useConvertQuoteToInvoice } from "../../hooks/useQuoteQueries";
import api from "../../api/axios"; // for live client fetch

// Compose & send work emails (prefill happens inside modal)
import SendWorkModal from "../../components/work/SendWorkModal";

import WorkDetailsUI from "./WorkDetails.ui";

const workNumberRegex = /^(TAS|JOB|APPT)-\d{4}-\d+$/i;

const idStr = (v) => (v == null ? "" : String(v));
const role = () => (localStorage.getItem("role") || "admin").toLowerCase();
const canMutateRole = () => ["admin", "staff"].includes(role());

/* ----------------------------- inline helpers ----------------------------- */
const fmtAddressMultiline = (addr) => {
  if (!addr) return "";
  if (typeof addr === "string") return addr;
  if (addr.raw) return String(addr.raw);

  const line1 = addr.line1 || addr.street || addr.street1 || "";
  const line2 = addr.line2 || addr.street2 || "";
  const city = addr.city || "";
  const region = addr.state || addr.region || addr.province || "";
  const postal = addr.postalCode || addr.zip || "";
  const country = addr.country || "";

  const rows = [];
  if (line1) rows.push(String(line1).trim());
  if (line2) rows.push(String(line2).trim());
  const cityRow = [city, region, postal].filter(Boolean).join(", ");
  if (cityRow) rows.push(cityRow);
  if (country) rows.push(country);

  return rows.join("\n");
};

const normalizeWorkAddress = (a) => {
  if (!a) return null;
  return {
    label: a.label ?? a.name ?? a.nickname ?? undefined,
    street: a.street ?? a.line1 ?? a.address1 ?? a.addressLine1 ?? undefined,
    line2: a.line2 ?? a.address2 ?? a.addressLine2 ?? a.unit ?? a.suite ?? undefined,
    city: a.city ?? undefined,
    state: a.state ?? a.region ?? a.province ?? undefined,
    postalCode: a.postalCode ?? a.postal ?? a.zip ?? a.zipcode ?? undefined,
    country: a.country ?? undefined,
    lat: a.lat ?? a.latitude ?? undefined,
    lng: a.lng ?? a.lon ?? a.long ?? a.longitude ?? undefined,
  };
};

/**
 * Normalize email type for modal + service.
 * Canonical types are: 'confirm' | 'reminder' | 'complete'
 * (service also accepts 'completed'/'completion' and normalizes → 'complete')
 */
const normalizeEmailType = (t) => {
  const s = String(t || "confirm").toLowerCase();
  if (s === "completed" || s === "completion") return "complete";
  if (s === "confirm" || s === "reminder" || s === "complete") return s;
  return "confirm";
};

export default function WorkDetailsPage() {
  const params = useParams();
  const navigate = useNavigate();

  const paramKey = params.workKey ?? params.id ?? "";

  const [resolvedId, setResolvedId] = useState(() =>
    workNumberRegex.test(paramKey || "") ? "" : paramKey || ""
  );
  const [resolving, setResolving] = useState(workNumberRegex.test(paramKey || ""));

  useEffect(() => {
    let active = true;
    async function resolve() {
      if (!workNumberRegex.test(paramKey || "")) return;
      try {
        setResolving(true);
        const item = await getWorkByNumber(String(paramKey || "").trim());
        const id = idStr(item?.id || item?._id);
        if (active) setResolvedId(id);
      } catch (e) {
        toast.error(e?.message || "Work not found");
      } finally {
        if (active) setResolving(false);
      }
    }
    resolve();
    return () => {
      active = false;
    };
  }, [paramKey]);

  /* ─────────────── Fetch ─────────────── */

  const enabled = Boolean(resolvedId && !resolving);
  const { data: work, isLoading, isError, refetch: refetchWork } = useWork(resolvedId, { enabled });

  const showChildren = enabled && work?.type === "job";
  const {
    items: childTasksRaw = [],
    isLoading: loadingTasks,
    refetch: refetchTasks,
  } = useChildWorks(resolvedId, { type: "task", enabled: showChildren });

  const {
    items: childApptsRaw = [],
    isLoading: loadingAppts,
    refetch: refetchAppts,
  } = useChildWorks(resolvedId, { type: "appointment", enabled: showChildren });

  const childTasks = useMemo(
    () =>
      Array.isArray(childTasksRaw)
        ? childTasksRaw.filter((w) => String(w.parentWorkId || "") === String(resolvedId))
        : [],
    [childTasksRaw, resolvedId]
  );
  const childAppointments = useMemo(
    () =>
      Array.isArray(childApptsRaw)
        ? childApptsRaw.filter((w) => String(w.parentWorkId || "") === String(resolvedId))
        : [],
    [childApptsRaw, resolvedId]
  );

  // Prefer snapshot id, then direct clientId on work, then links.clientId
  const clientIdForLink = useMemo(() => {
    const fromSnap = idStr(work?.clientSnapshot?.id);
    const fromWork = idStr(work?.clientId);
    const fromLinks = idStr(work?.links?.clientId);
    return fromSnap || fromWork || fromLinks || null;
  }, [work?.clientSnapshot?.id, work?.clientId, work?.links?.clientId]);

  /* Live client fetch as fallback for missing snapshot fields */
  const [client, setClient] = useState(null);
  const [loadingClient, setLoadingClient] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function loadClient() {
      if (!enabled || !clientIdForLink) {
        setClient(null);
        return;
      }
      try {
        setLoadingClient(true);
        const res = await api.get(`/clients/${clientIdForLink}`);
        if (!cancelled) setClient(res?.data?.data || res?.data || null);
      } catch {
        if (!cancelled) setClient(null);
      } finally {
        if (!cancelled) setLoadingClient(false);
      }
    }
    loadClient();
    return () => {
      cancelled = true;
    };
  }, [enabled, clientIdForLink]);

  /* Optional pool kept for quick link */
  const { items: linkPool = [] } = useWorkList(
    clientIdForLink ? { clientId: clientIdForLink, type: "task", page: 1, limit: 50 } : {},
    { enabled: !!clientIdForLink, keepPreviousData: true, staleTime: 10_000 }
  );

  const linkCandidates = useMemo(() => {
    if (!Array.isArray(linkPool)) return [];
    const closedTask = new Set(["done", "cancelled"]);
    return linkPool
      .filter((w) => w.id !== resolvedId)
      .filter((w) => !w.parentWorkId)
      .filter((w) => w.type === "task" && !closedTask.has(w.status));
  }, [linkPool, resolvedId]);

  /* ─────────────── Mutations ─────────────── */

  const { mutateAsync: updateWork, isPending: savingAny } = useUpdateWork();
  const { mutateAsync: transitionWork, isPending: transitioning } = useTransitionWork();
  const { mutateAsync: deleteWork, isPending: deleting } = useDeleteWork();
  const { mutateAsync: attachInvoice, isPending: attachingInvoice } = useAttachInvoice();
  const { mutateAsync: createWork } = useCreateWork();

  const quoteIdForHook = work?.links?.quoteId ? String(work.links.quoteId) : null;
  const { mutateAsync: convertQuoteToInvoice, isPending: converting } =
    useConvertQuoteToInvoice(quoteIdForHook, {});

  const refreshAll = useCallback(() => {
    refetchWork();
    if (showChildren) {
      refetchTasks();
      refetchAppts();
    }
  }, [refetchWork, showChildren, refetchTasks, refetchAppts]);

  /* ─────────────── Flags ─────────────── */

  const softDeleted = !!(work?.softDeleted || work?.meta?.softDeleted);
  const canMutate = canMutateRole();

  /* ─────────────── Actions ─────────────── */

  const onBack = useCallback(() => navigate(-1), [navigate]);

  const onSaveBasics = useCallback(
    async ({ title, priority }) => {
      if (!enabled || !canMutate || softDeleted) return;
      try {
        const patch = {};
        if (typeof title === "string") patch.title = title.trim();
        if (priority) patch.priority = String(priority);
        if (!Object.keys(patch).length) return;
        await updateWork({ id: resolvedId, patch });
        refetchWork();
      } catch (e) {
        toast.error(e?.message || "Failed to save basics");
      }
    },
    [enabled, canMutate, softDeleted, updateWork, resolvedId, refetchWork]
  );

  const onSaveSchedule = useCallback(
    async ({ start, end, allDay, timezone }) => {
      if (!enabled || !canMutate || softDeleted) return;
      try {
        const nextStart = start ? new Date(start) : null;
        const nextEnd = end ? new Date(end) : null;

        if (nextStart && nextEnd && nextEnd < nextStart) {
          throw new Error("End time cannot be before start time.");
        }

        const tz = timezone || work?.schedule?.timezone || null;

        const schedulePatch = {
          start: nextStart ? nextStart.toISOString() : null,
          end: nextEnd ? nextEnd.toISOString() : null,
          allDay: !!allDay,
        };
        if (tz) {
          schedulePatch.timezone = tz;
        }

        await updateWork({ id: resolvedId, patch: { schedule: schedulePatch } });
        toast.success("Schedule saved");
        refetchWork();
      } catch (e) {
        toast.error(e?.message || "Failed to save schedule");
      }
    },
    [enabled, canMutate, softDeleted, updateWork, resolvedId, refetchWork, work?.schedule?.timezone]
  );

  const onSaveTags = useCallback(
    async (tagsArray) => {
      if (!enabled || !canMutate || softDeleted) return;
      try {
        const tags = Array.isArray(tagsArray) ? tagsArray.filter(Boolean) : [];
        await updateWork({ id: resolvedId, patch: { tags } });
        toast.success("Tags saved");
        refetchWork();
      } catch (e) {
        toast.error(e?.message || "Failed to save tags");
      }
    },
    [enabled, canMutate, softDeleted, updateWork, resolvedId, refetchWork]
  );

  const onSaveDescription = useCallback(
    async (description) => {
      if (!enabled || !canMutate || softDeleted) return;
      try {
        await updateWork({ id: resolvedId, patch: { description } });
        refetchWork();
      } catch (e) {
        toast.error(e?.message || "Failed to save notes");
      }
    },
    [enabled, canMutate, softDeleted, updateWork, resolvedId, refetchWork]
  );

  const onSaveAddress = useCallback(
    async (addressObj) => {
      if (!enabled || !canMutate || softDeleted) return;
      try {
        const normalized = normalizeWorkAddress(addressObj);
        await updateWork({ id: resolvedId, patch: { address: normalized } });
        toast.success("Address saved");
        refetchWork();
      } catch (e) {
        toast.error(e?.message || "Failed to save address");
      }
    },
    [enabled, canMutate, softDeleted, updateWork, resolvedId, refetchWork]
  );

  const onSaveJobProfile = useCallback(
    async (jobProfilePatch) => {
      if (!enabled || !canMutate || softDeleted) return;
      try {
        await updateWork({ id: resolvedId, patch: { jobProfile: jobProfilePatch || {} } });
        toast.success("Job profile saved");
        refetchWork();
      } catch (e) {
        toast.error(e?.message || "Failed to save job profile");
      }
    },
    [enabled, canMutate, softDeleted, updateWork, resolvedId, refetchWork]
  );

  const onSaveAssignees = useCallback(
    async (assigneeIds = []) => {
      if (!enabled || !canMutate || softDeleted) return;
      try {
        const assignees = (assigneeIds || []).map(String);
        await updateWork({ id: resolvedId, patch: { assignees } });
        toast.success("Assignees updated");
        refetchWork();
      } catch (e) {
        toast.error(e?.message || "Failed to update assignees");
      }
    },
    [enabled, canMutate, softDeleted, updateWork, resolvedId, refetchWork]
  );

  const onTransition = useCallback(
    async (nextStatus) => {
      if (!enabled || !canMutate || softDeleted) return;
      if (!nextStatus) return;
      if (work?.type === "job" && String(nextStatus) === "invoiced" && !work?.links?.invoiceId) {
        toast.error("Link an invoice before marking the job as Invoiced.");
        return;
      }
      try {
        await transitionWork({ id: resolvedId, status: nextStatus });
        toast.success("Status updated");
        refetchWork();
      } catch (e) {
        toast.error(e?.message || "Failed to update status");
      }
    },
    [enabled, canMutate, softDeleted, transitionWork, resolvedId, refetchWork, work?.type, work?.links?.invoiceId]
  );

  const onDelete = useCallback(async () => {
    if (!enabled || !canMutate || softDeleted) return;
    const ok = window.confirm(
      "Delete this item? This is a soft delete and can be restored by admins."
    );
    if (!ok) return;
    try {
      await deleteWork(resolvedId);
      toast.success("Deleted");
      navigate("/work");
    } catch (e) {
      toast.error(e?.message || "Failed to delete");
    }
  }, [enabled, canMutate, softDeleted, deleteWork, resolvedId, navigate]);

  const onCreateInvoice = useCallback(async () => {
    if (!work || !canMutate || softDeleted) return;
    const clientId = idStr(work?.clientSnapshot?.id || "");
    const workId = idStr(work?.id);
    const quoteId = work?.links?.quoteId ? String(work.links.quoteId) : "";
    if (quoteId) {
      try {
        const result = await convertQuoteToInvoice({ workId, clientId });
        const newInvoiceId =
          idStr(
            result?.invoiceId ||
              result?.id ||
              result?._id ||
              result?.invoice?._id ||
              result?.data?.invoiceId ||
              result?.data?.invoice?._id
          ) || "";
        if (!newInvoiceId) throw new Error("Invoice created but no invoiceId was returned.");
        await attachInvoice({ id: workId, invoiceId: newInvoiceId });
        toast.success("Invoice created and linked");
        refetchWork();
      } catch (e) {
        const status = e?.status || e?.response?.status;
        const serverMsg = e?.message || e?.response?.data?.message;
        if (status === 409) toast.info("This quote was already converted to an invoice.");
        else toast.error(serverMsg || "Failed to convert quote to invoice");
      }
    } else {
      const url = `/invoices/new?clientId=${encodeURIComponent(
        clientId
      )}&workId=${encodeURIComponent(workId)}`;
      window.location.assign(url);
    }
  }, [work, canMutate, softDeleted, convertQuoteToInvoice, attachInvoice, refetchWork]);

  const onOpenEntity = useCallback((kind, idOrNumber) => {
    if (!idOrNumber) return;
    const urlMap = {
      client: `/clients/${encodeURIComponent(String(idOrNumber))}`,
      quote: `/quotes/${encodeURIComponent(String(idOrNumber))}`,
      invoice: `/invoices/${encodeURIComponent(String(idOrNumber))}`,
      work: `/work/${encodeURIComponent(String(idOrNumber))}`,
    };
    const url = urlMap[kind];
    if (url) window.location.assign(url);
  }, []);

  /* ─────────────── Inline children (search/link/create) ─────────────── */

  const onChildSearch = useCallback(
    async ({ type, q }) => {
      try {
        const clientId = clientIdForLink;
        if (!clientId) return [];
        const qs = new URLSearchParams();
        qs.set("clientId", clientId);
        qs.set("type", String(type || ""));
        if (q) qs.set("q", String(q || ""));
        qs.set("page", "1");
        qs.set("limit", "20");
        const res = await fetch(`/api/works?${qs.toString()}`, { credentials: "include" });
        if (!res.ok) return [];
        const payload = await res.json();
        const items = payload?.items || payload?.data?.items || payload?.data || [];
        const closedTask = new Set(["done", "cancelled"]);
        const closedAppt = new Set(["done", "cancelled", "no_show"]);
        return (Array.isArray(items) ? items : [])
          .filter((w) => w.id !== resolvedId)
          .filter((w) => !w.parentWorkId)
          .filter((w) => {
            if (type === "task") return !closedTask.has(w.status);
            if (type === "appointment") return !closedAppt.has(w.status);
            return false;
          });
      } catch {
        return [];
      }
    },
    [clientIdForLink, resolvedId]
  );

  const onCreateChild = useCallback(
    async (type, { title, start, duration, timezone }) => {
      if (!enabled || !canMutate || softDeleted) return;
      try {
        const payload = {
          type,
          title: String(title || "").trim(),
          parentWorkId: resolvedId,
          clientId: work?.clientSnapshot?.id ? String(work.clientSnapshot.id) : undefined,
        };

        if (type === "appointment") {
          if (!start) {
            toast.error("Start date/time is required for appointments.");
            return;
          }
          const startDate = new Date(start);
          const endDate = new Date(
            startDate.getTime() + Number(duration || 60) * 60000
          );
          const schedule = {
            start: startDate.toISOString(),
            end: endDate.toISOString(),
            allDay: false,
          };
          const tz = timezone || work?.schedule?.timezone || null;
          if (tz) {
            schedule.timezone = tz;
          }
          payload.schedule = schedule;
        }

        await createWork(payload);
        toast.success(`${type === "task" ? "Task" : "Appointment"} created`);
        if (showChildren) {
          refetchTasks();
          refetchAppts();
        }
      } catch (e) {
        toast.error(e?.message || "Failed to create");
      }
    },
    [
      enabled,
      canMutate,
      softDeleted,
      resolvedId,
      work?.clientSnapshot?.id,
      work?.schedule?.timezone,
      createWork,
      showChildren,
      refetchTasks,
      refetchAppts,
    ]
  );

  const onLinkChild = useCallback(
    async ({ childId }) => {
      if (!enabled || !canMutate || softDeleted) return;
      try {
        await updateWork({ id: childId, patch: { parentWorkId: resolvedId } });
        toast.success("Linked");
        if (showChildren) {
          refetchTasks();
          refetchAppts();
        }
      } catch (e) {
        toast.error(e?.message || "Failed to link");
      }
    },
    [enabled, canMutate, softDeleted, resolvedId, updateWork, showChildren, refetchTasks, refetchAppts]
  );

  const onUnlinkChild = useCallback(
    async (childId) => {
      if (!enabled || !canMutate || softDeleted) return;
      try {
        await updateWork({ id: childId, patch: { parentWorkId: null } });
        toast.success("Unlinked");
        if (showChildren) {
          refetchTasks();
          refetchAppts();
        }
      } catch (e) {
        toast.error(e?.message || "Failed to unlink");
      }
    },
    [enabled, canMutate, softDeleted, resolvedId, updateWork, showChildren, refetchTasks, refetchAppts]
  );

  /* ─────────────── Send work emails (Modal flow) ─────────────── */

  const [sendOpen, setSendOpen] = useState(false);
  const [sendType, setSendType] = useState(null); // 'confirm' | 'reminder' | 'complete'

  const onRequestSend = useCallback((type) => {
    setSendType(normalizeEmailType(type || "confirm"));
    setSendOpen(true);
  }, []);

  // Best-effort last-sent timestamp for modal hints/idempotency text
  const lastWorkNoticeAt =
    work?.comms?.lastWorkNoticeAt ||
    work?.comms?.lastSentAt ||
    work?.meta?.lastWorkNoticeAt ||
    null;

  /* ─────────────── Derived helpers ─────────────── */

  const allowedNext = Array.isArray(work?.allowedNextStatuses) ? work.allowedNextStatuses : [];
  const primaryNext = allowedNext[0] || null;

  const assigneeOptions = useMemo(() => {
    const fromMeta =
      Array.isArray(work?.meta?.assigneeOptions)
        ? work.meta.assigneeOptions
        : Array.isArray(work?.meta?.teamUsers)
        ? work.meta.teamUsers
        : Array.isArray(work?.assigneeDirectory)
        ? work.assigneeDirectory
        : [];

    return fromMeta
      .map((u) => ({
        id: idStr(u?.id || u?._id),
        name:
          u?.name ||
          u?.fullName ||
          (u?.firstName && u?.lastName
            ? `${u.firstName} ${u.lastName}`.trim()
            : u?.email || "User"),
        email: u?.email || "",
      }))
      .filter((u) => u.id);
  }, [work?.meta?.assigneeOptions, work?.meta?.teamUsers, work?.assigneeDirectory]);

  /* ─────────────── Bill To (client) derived for UI ─────────────── */
  const billTo = useMemo(() => {
    const snap = work?.clientSnapshot || {};
    const address =
      snap.address ||
      client?.billingAddress ||
      client?.address ||
      work?.billTo?.address ||
      null;

    return {
      company: snap.name || client?.name || work?.clientName || "—",
      contactName:
        snap.contactName ||
        client?.primaryContact?.name ||
        client?.contactName ||
        client?.contact?.fullName ||
        client?.contact?.name ||
        "",
      email:
        snap.email ||
        client?.primaryContact?.email ||
        client?.email ||
        client?.contact?.email ||
        "",
      phone:
        snap.phone ||
        client?.primaryContact?.phone ||
        client?.phone ||
        client?.contact?.phone ||
        "",
      addressText: fmtAddressMultiline(address),
      clientId: clientIdForLink,
    };
  }, [work?.clientSnapshot, work?.billTo?.address, work?.clientName, client, clientIdForLink]);

  /* ─────────────── Render ─────────────── */

  return (
    <>
      <WorkDetailsUI
        // data/meta
        work={work}
        resolving={resolving}
        isLoading={isLoading}
        isError={isError}
        softDeleted={softDeleted}
        canMutate={canMutate}
        role={role()}

        // header/meta actions
        onBack={onBack}

        // saves
        onSaveBasics={onSaveBasics}
        onSaveSchedule={onSaveSchedule}
        onSaveTags={onSaveTags}
        onSaveDescription={onSaveDescription}
        onSaveAddress={onSaveAddress}
        onSaveAssignees={onSaveAssignees}

        // status
        onTransition={onTransition}
        transitioning={transitioning}
        primaryNext={primaryNext}

        // destructive
        onDelete={onDelete}
        deleting={deleting}

        // quote/invoice (used by sidebar)
        onCreateInvoice={onCreateInvoice}
        converting={converting}
        attachingInvoice={attachingInvoice}

        // open entity
        onOpenEntity={onOpenEntity}

        // children
        childTasks={childTasks}
        childAppointments={childAppointments}
        loadingChildTasks={loadingTasks}
        loadingChildAppointments={loadingAppts}
        onCreateChild={onCreateChild}
        onLinkChild={onLinkChild}
        onUnlinkChild={onUnlinkChild}
        onChildSearch={onChildSearch}

        // job profile inline save
        onSaveJobProfile={onSaveJobProfile}

        // ✅ preferred: header triggers the modal (no direct-send props)
        onRequestSend={onRequestSend}

        // header controls data
        assigneeOptions={assigneeOptions}
        savingBasics={savingAny}
        savingAssignees={savingAny}
        savingAny={savingAny}

        // client info for UI
        billTo={billTo}
        client={client}
        loadingClient={loadingClient}
      />

      {/* Send Work Modal */}
      {sendOpen && (
        <SendWorkModal
          isOpen={sendOpen}
          onClose={() => setSendOpen(false)}
          work={work}
          type={sendType} // already normalized
          lastSentAt={lastWorkNoticeAt}
          // modal performs the send; keep it the single source of toasts
          onSend={async (t, payload) => {
            const tt = normalizeEmailType(t || sendType);
            return sendWorkNotice(resolvedId, tt, payload);
          }}
          onSent={() => {
            // ❌ no toasts here → modal already shows one
            setSendOpen(false);
            refreshAll();
          }}
        />
      )}
    </>
  );
}
